import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthPageShell from '../components/AuthPageShell';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getNextAuthRoute } from '../utils/authFlow';

export default function VerifyEmail() {
  const {
    isAuthenticated,
    isEmailVerified,
    isLoading,
    isMfaChallengeRequired,
    isMfaEnrolled,
    logout,
    refreshUser,
    sendVerificationEmail,
    user,
  } = useAuth();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  const nextRoute = getNextAuthRoute({
    isAuthenticated,
    isEmailVerified,
    isMfaChallengeRequired,
    isMfaEnrolled,
  });

  if (nextRoute !== '/verify-email') {
    return <Navigate to={nextRoute} replace />;
  }

  const handleSendVerification = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await sendVerificationEmail();
      setMessage('Verification email sent. Open the link in your inbox, then come back here.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send verification email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await refreshUser();
      setMessage('Account status refreshed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh account status.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      await logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to logout.');
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageShell
      title="Verify Your Email"
      description="Firebase requires a verified email before this dashboard can enroll a second factor."
    >
      <div className="space-y-4">
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-4 text-sm text-blue-900 dark:text-blue-200">
          <p className="font-medium mb-1">Signed in as</p>
          <p className="break-all">{user?.email ?? 'Unknown email'}</p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          1. Send or reopen the verification email.
          <br />
          2. Click the verification link in your inbox.
          <br />
          3. Return here and refresh your account status.
        </p>
        {error ? (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="rounded-md bg-green-50 dark:bg-green-950/30 p-3 text-sm text-green-700 dark:text-green-300">
            {message}
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleSendVerification}
          className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Send verification email
        </button>
        <button
          type="button"
          onClick={handleRefresh}
          className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          I verified my email
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full text-sm text-gray-600 dark:text-gray-300 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          Logout
        </button>
      </div>
    </AuthPageShell>
  );
}
